import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import { ThemeContext } from "../../src/ThemeContext";
import { FaPlusCircle } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { MdDeleteOutline } from "react-icons/md";
import ReactSelect from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import "../tables/expense.css";
import "../forms/UserForm.css";
import "../Responsive/TableResponsive.css";
import "../Responsive/FormResponsive.css";
import Select from "react-select";

// ── Category options ───────────────────────────────────────────────────────────
const expenseCategoryOptions = [
  { value: "IOCL Depot",      label: "IOCL Depot" },
  { value: "Keshav Encon",    label: "Keshav Encon" },
  { value: "Travel",          label: "Travel" },
  { value: "Fuel",            label: "Fuel" },
  { value: "Accommodation",   label: "Accommodation" },
  { value: "Food",            label: "Food" },
  { value: "Miscellaneous",   label: "Miscellaneous" },
  { value: "Office Supplies", label: "Office Supplies" },
  { value: "Communication",   label: "Communication" },
  { value: "General",         label: "General" },
];

const emptyNewItem = () => ({
  expense_date: null,
  bill_receipt_no: "",
  amount: "",
  purpose_description: "",
  vendor_remark: "",
  expense_category: "General",
});

function ExpenseEdit({ isSidebarExpanded }) {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { theme } = useContext(ThemeContext);

  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [isSaving, setIsSaving]     = useState(false);
  const [users, setUsers]           = useState([]);
  const [existingItems, setExistingItems]     = useState([]);
  const [newItems, setNewItems]               = useState([]);
  const [deletedItemIds, setDeletedItemIds]   = useState([]);

  const [editReport, setEditReport] = useState({
    report_month: "",
    location: "",
    advance_amount: "",
    balance_brought_forward: "",
    claim_period_start: null,
    claim_period_end: null,
    user_id: "",
  });

  const [snackbarOpen, setSnackbarOpen]       = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity]               = useState("success");

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSeverity(sev);
    setSnackbarOpen(true);
  };

  // ── react-select styles ────────────────────────────────────────────────────
  const selectStyles1 = {
    container: (provided) => ({
      ...provided,
      width: "28%",
    }),
    control: (p) => ({
      ...p,
      backgroundColor: theme === "dark" ? "#000" : "#fff",
      borderColor: theme === "dark" ? "#444" : "#ccc",
      boxShadow: "none",
      minHeight: "34px",
      "&:hover": { borderColor: theme === "dark" ? "#666" : "#888" },
    }),
    menu: (p) => ({
      ...p,
      backgroundColor: theme === "dark" ? "#000" : "#fff",
      border: `1px solid ${theme === "dark" ? "#444" : "#ccc"}`,
      zIndex: 999,
    }),
    option: (p, state) => ({
      ...p,
      backgroundColor: state.isFocused ? "#666" : theme === "dark" ? "#000" : "#fff",
      color: state.isFocused ? "#fff" : theme === "dark" ? "#fff" : "#333",
      cursor: "pointer",
    }),
    singleValue: (p) => ({ ...p, color: theme === "dark" ? "#fff" : "#333" }),
  };

  const selectStyles = {
    container: (provided) => ({
      ...provided,
      width: "100%",
      marginTop: "15px !important",
    }),

    control: (provided) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "black" : "white", // Dropdown background color
      color: theme === "dark" ? "#fff" : "#333", // Text color
      borderColor: theme === "dark" ? "#444" : "#ccc", // Border color
      cursor: "pointer",
      boxShadow: "none",
      "&:hover": {
        borderColor: theme === "dark" ? "#666" : "#888", // Border color on hover
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#000" : "#fff", // Black background for dropdown menu in dark mode
      border: `1px solid ${theme === "dark" ? "#444" : "#ccc"}`, // Adding a border to the opened menu
      boxShadow:
        theme === "dark"
          ? "0 4px 6px rgba(0, 0, 0, 0.6)"
          : "0 4px 6px rgba(0, 0, 0, 0.1)", // Optional shadow for better separation
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? "#666" // Gray when hovered
        : theme === "dark"
          ? "#000"
          : "#fff", // Black background for options in dark mode
      color: state.isFocused ? "#fff" : theme === "dark" ? "#fff" : "#333", // Text color
      cursor: "pointer",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme === "dark" ? "#fff" : "#333", // Selected option text color
    }),
  };

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const res = await axios.get(`${API_URL}/api/get_user`, {
        headers: { Authorization: authKey },
      });
      if (Array.isArray(res.data)) setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err.message);
    }
  }, []);

  // ── Fetch report ───────────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const res = await axios.get(`${API_URL}/api/expense_report/${id}`, {
        headers: { Authorization: authKey },
      });
      if (res.status === 200) {
        const r = res.data.report;
        setReport(r);
        setEditReport({
          report_month: r.report_month || "",
          location: r.location || "",
          advance_amount: r.advance_amount ?? "",
          balance_brought_forward: r.balance_brought_forward ?? "",
          claim_period_start: r.claim_period_start ? new Date(r.claim_period_start) : null,
          claim_period_end:   r.claim_period_end   ? new Date(r.claim_period_end)   : null,
          user_id: r.user_id || "",
        });
        setExistingItems(
          (res.data.items || []).map((it) => ({
            ...it,
            expense_date: it.expense_date ? new Date(it.expense_date) : null,
          }))
        );
        setNewItems([]);
        setDeletedItemIds([]);
      }
    } catch {
      showSnackbar("Error fetching expense report", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUsers();
    if (id) fetchReport();
  }, [id]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleReportChange = (field, value) =>
    setEditReport((prev) => ({ ...prev, [field]: value }));

  const handleExistingItemChange = (index, field, value) =>
    setExistingItems((prev) => {
      const u = [...prev];
      u[index] = { ...u[index], [field]: value };
      return u;
    });

  const handleDeleteExistingItem = (itemId) => {
    setDeletedItemIds((prev) => [...prev, itemId]);
    setExistingItems((prev) => prev.filter((it) => it.id !== itemId));
  };

  const handleNewItemChange = (index, field, value) =>
    setNewItems((prev) => {
      const u = [...prev];
      u[index] = { ...u[index], [field]: value };
      return u;
    });

  const handleRemoveNewItem = (index) =>
    setNewItems((prev) => prev.filter((_, i) => i !== index));

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editReport.report_month) {
      showSnackbar("Report month is required", "warning");
      return;
    }
    for (let i = 0; i < existingItems.length; i++) {
      if (!existingItems[i].amount || isNaN(parseFloat(existingItems[i].amount))) {
        showSnackbar(`Amount is required for item ${i + 1}`, "warning");
        return;
      }
    }
    for (let i = 0; i < newItems.length; i++) {
      if (!newItems[i].amount || isNaN(parseFloat(newItems[i].amount))) {
        showSnackbar(`Amount is required for new item ${i + 1}`, "warning");
        return;
      }
    }

    try {
      setIsSaving(true);
      const authKey = localStorage.getItem("authKey");

      // 1 — update header
      const reportPayload = {
        report_month: editReport.report_month,
        location: editReport.location || null,
        advance_amount: parseFloat(editReport.advance_amount) || 0,
        balance_brought_forward: parseFloat(editReport.balance_brought_forward) || 0,
        claim_period_start: editReport.claim_period_start
          ? format(new Date(editReport.claim_period_start), "yyyy-MM-dd")
          : null,
        claim_period_end: editReport.claim_period_end
          ? format(new Date(editReport.claim_period_end), "yyyy-MM-dd")
          : null,
      };
      if (editReport.user_id) reportPayload.user_id = editReport.user_id;

      await axios.put(`${API_URL}/api/expense_report/${id}`, reportPayload, {
        headers: { Authorization: authKey, "Content-Type": "application/json" },
      });

      // 2 — update existing items
      for (const item of existingItems) {
        await axios.put(
          `${API_URL}/api/expense_report/${id}/items/${item.id}`,
          {
            expense_date: item.expense_date
              ? format(new Date(item.expense_date), "yyyy-MM-dd")
              : null,
            bill_receipt_no: item.bill_receipt_no || null,
            amount: parseFloat(item.amount),
            purpose_description: item.purpose_description || null,
            vendor_remark: item.vendor_remark || null,
            expense_category: item.expense_category || "General",
          },
          { headers: { Authorization: authKey, "Content-Type": "application/json" } }
        );
      }

      // 3 — delete removed items
      for (const itemId of deletedItemIds) {
        await axios.delete(
          `${API_URL}/api/expense_report/${id}/items/${itemId}`,
          { headers: { Authorization: authKey } }
        );
      }

      // 4 — add new items in a single call
      if (newItems.length > 0) {
        await axios.post(
          `${API_URL}/api/expense_report/${id}/items`,
          {
            items: newItems.map((item) => ({
              expense_date: item.expense_date
                ? format(item.expense_date, "yyyy-MM-dd")
                : null,
              bill_receipt_no: item.bill_receipt_no || null,
              amount: parseFloat(item.amount),
              purpose_description: item.purpose_description || null,
              vendor_remark: item.vendor_remark || null,
              expense_category: item.expense_category || "General",
            })),
          },
          { headers: { Authorization: authKey, "Content-Type": "application/json" } }
        );
      }

      showSnackbar("Expense report updated successfully");
      setTimeout(() => navigate("/all-expenses"), 1200);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Error updating report", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totalAmount = [...existingItems, ...newItems].reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );

  const formatCurrency = (val) =>
    `₹${parseFloat(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const userOptions = users.map((u) => ({ value: u.ID, label: u.Name }));

  // ── Reusable item row renderer ─────────────────────────────────────────────
  const ItemRow = ({ item, index, isNew, onFieldChange, onDelete }) => (
    <div
      key={isNew ? `new-${index}` : item.id || index}
      className="location-group-content"
    >
      <div className="location-group margin_left_expenses">
        {isNew && <div className="expense_new_item_badge">+ NEW ITEM</div>}

        {/* Row 1: date / receipt / amount / category */}
        <div className="flex-group" style={{ flexWrap: "wrap", gap: "12px", width: "100%" }}>
          {/* Date */}
          <div>
            <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px" }}>
              Date
            </label>
            <div className="input-group frequency-row">
              <DatePicker
                selected={item.expense_date ? new Date(item.expense_date) : null}
                onChange={(date) => onFieldChange(index, "expense_date", date)}
                placeholderText="Select date"
                className="dates point-fr"
                dateFormat="yyyy-MM-dd"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </div>
          </div>

          {/* Bill No */}
          <div>
            <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px", width: "100%" }}>
              Bill / Receipt No
            </label>
            <div className="input-group frequency-row">
              <input
                type="text"
                className="point-fr"
                value={item.bill_receipt_no || ""}
                onChange={(e) => onFieldChange(index, "bill_receipt_no", e.target.value)}
                placeholder="Bill / Receipt No"
              />
            </div>
          </div>

          {/* Amount */}
          <div>
            <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px", width: "100%" }}>
              Amount (₹)<span className="text-danger">*</span>
            </label>
            <div className="input-group frequency-row">
              <input
                type="number"
                className="point-fr"
                value={item.amount || ""}
                onChange={(e) => onFieldChange(index, "amount", e.target.value)}
                placeholder="₹ 0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Category */}
          <div style={{ width: "48%" }}>
            <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px", width: "100%" }}>
              Expense Type
            </label>
            <ReactSelect
              options={expenseCategoryOptions}
              value={expenseCategoryOptions.find((o) => o.value === item.expense_category) || null}
              onChange={(opt) => onFieldChange(index, "expense_category", opt?.value || "General")}
              styles={selectStyles}
              placeholder="Select type..."
            />
          </div>
        </div>

        {/* Row 2: purpose / vendor */}
        <div className="flex-group" style={{ marginTop: "10px", gap: "12px" }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px" , width: "100%"}}>
              Purpose / Description
            </label>
            <div className="input-group frequency-row">
              <textarea
                className="point-fr"
                value={item.purpose_description || ""}
                onChange={(e) => onFieldChange(index, "purpose_description", e.target.value)}
                placeholder="Enter purpose or description"
                rows="2"
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "4px", width: "100%" }}>
              Vendor / Remark
            </label>
            <div className="input-group frequency-row">
              <textarea
                className="point-fr"
                value={item.vendor_remark || ""}
                onChange={(e) => onFieldChange(index, "vendor_remark", e.target.value)}
                placeholder="Enter vendor or remark"
                rows="2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delete / Remove button */}
      <div>
        <Tooltip title={isNew ? "Remove" : "Delete item"} arrow>
          <button
            type="button"
            className="add-close-button"
            onClick={() => onDelete(isNew ? index : item.id)}
          >
            {isNew
              ? <IoClose className="frequency-close-icon" />
              : <MdDeleteOutline style={{ fontSize: "18px", color: "#e53935" }} />
            }
          </button>
        </Tooltip>
      </div>
    </div>
  );

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`data-form-content-container page-content ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
        <div className="loader-container"><div className="loader"></div><div>Loading...</div></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className={`data-form-content-container page-content ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
        <div className="no-users-container"><div className="no-users-message">Expense Report Not Found</div></div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">

          {/* Header */}
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Edit Expense Report</PageBackTitle>
              <div className="data-form-heading-content-right-side" />
            </div>
          </div>

          <div className="header-separator"></div>

          <div className="data-form-body">
            <form
              className="form-detail"
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            >
              <div className="new-data-form">

                {/* Employee */}
                <div className="form-group-select">
                  <label>Employee</label>
                    <Select
                      options={userOptions}
                      value={userOptions.find((u) => u.value === editReport.user_id) || null}
                      onChange={(opt) => handleReportChange("user_id", opt?.value || "")}
                      styles={selectStyles1}
                      placeholder="Select..."
                      isClearable
                      isDisabled
                    />
                </div>

                {/* Report Month */}
                <div className="form-group">
                  <label>
                    Report Month<span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      type="month"
                      value={editReport.report_month}
                      onChange={(e) => handleReportChange("report_month", e.target.value)}
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="form-group">
                  <label>Location / Site</label>
                  <div className="input-group">
                    <input
                      type="text"
                      value={editReport.location}
                      onChange={(e) => handleReportChange("location", e.target.value)}
                      placeholder="Enter location or site"
                    />
                  </div>
                </div>

                {/* Advance Amount */}
                <div className="form-group">
                  <label>Advance Amount (₹)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      value={editReport.advance_amount}
                      onChange={(e) => handleReportChange("advance_amount", e.target.value)}
                      placeholder="Enter advance amount"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Balance Brought Forward */}
                <div className="form-group">
                  <label>Balance Brought Forward (₹)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      value={editReport.balance_brought_forward}
                      onChange={(e) =>
                        handleReportChange("balance_brought_forward", e.target.value)
                      }
                      placeholder="Enter balance brought forward"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Claim Period Start */}
                <div className="form-group">
                  <label>Claim Period Start</label>
                  <div className="input-group">
                    <DatePicker
                      selected={editReport.claim_period_start}
                      onChange={(date) => handleReportChange("claim_period_start", date)}
                      placeholderText="Select start date"
                      className="dates"
                      dateFormat="yyyy-MM-dd"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                    />
                  </div>
                </div>

                {/* Claim Period End */}
                <div className="form-group">
                  <label>Claim Period End</label>
                  <div className="input-group">
                    <DatePicker
                      selected={editReport.claim_period_end}
                      onChange={(date) => handleReportChange("claim_period_end", date)}
                      placeholderText="Select end date"
                      className="dates"
                      dateFormat="yyyy-MM-dd"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      minDate={editReport.claim_period_start}
                    />
                  </div>
                </div>

              </div>

              {/* ── Expense Items ── */}
              <div className="form-group" style={{ marginTop: "20px" }}>
                <div className="expense_form_section_label">
                  Expense Items<span className="text-danger">*</span>
                </div>

                <div className="new-frequency-rowss">
                  {existingItems.map((item, index) => (
                    <ItemRow
                      key={item.id || index}
                      item={item}
                      index={index}
                      isNew={false}
                      onFieldChange={handleExistingItemChange}
                      onDelete={handleDeleteExistingItem}
                    />
                  ))}

                  {newItems.map((item, index) => (
                    <ItemRow
                      key={`new-${index}`}
                      item={item}
                      index={index}
                      isNew={true}
                      onFieldChange={handleNewItemChange}
                      onDelete={handleRemoveNewItem}
                    />
                  ))}

<div className="expenses_dflex_total">
                  {/* Total */}
                  {(existingItems.length > 0 || newItems.length > 0) && (
                    <div className="expense_items_total">
                      Total: {formatCurrency(totalAmount)}
                    </div>
                  )}

                  {/* Add item */}
                  <div className="add-new-frequency-customer">
                    <button
                      className="add-new-buttons"
                      type="button"
                      onClick={() => setNewItems((prev) => [...prev, emptyNewItem()])}
                    >
                      <FaPlusCircle className="frequency-plus-icon" />
                    </button>
                  </div>
                    </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                onClick={() => navigate("/all-expenses")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ExpenseEdit;